/**
 * Veritabanından base64 olarak gelen dokümanın gerçek türünü içeriğin ilk byte'larından bulur.
 *
 * T_VERI_BANKA yalnızca PDF tutmuyor (DATAMIME / DOSYA_UZANTISI kolonları var); Apollo'dan
 * yüklenmemiş kayıtlar Word, Excel, görsel vb. olabiliyor. Bunlar PDF görüntüleyiciye
 * verildiğinde "Invalid PDF structure" hatası alınıyordu. Uzantı/mime kolonuna güvenmek yerine
 * içeriğe bakılıyor; yanlış etiketlenmiş kayıtlar da doğru açılır.
 */

const baslar = (b, imza, offset = 0) => imza.every((x, i) => b[offset + i] === x);

// Byte dizisi içinde ASCII metin arar (zip içindeki klasör adları için).
const icerir = (b, metin, sinir = b.length) => {
  const kodlar = Array.from(metin, c => c.charCodeAt(0));
  const son = Math.min(sinir, b.length) - kodlar.length;
  for (let i = 0; i <= son; i++) {
    if (baslar(b, kodlar, i)) return true;
  }
  return false;
};

// destekleniyor: Teknisyen Koçu'nda kullanılan türler (PDF, JPEG, PNG, Office). Diğerleri
// (GIF, zip, exe, boş, tanınmayan...) listelerde SP tarafında gizleniyor; yine de açılırsa
// içerik gösterilmez ve indirilemez.
const TURLER = {
  pdf: { tur: 'pdf', destekleniyor: true, mime: 'application/pdf', uzanti: '.pdf', etiket: 'PDF' },
  jpg: { tur: 'resim', destekleniyor: true, mime: 'image/jpeg', uzanti: '.jpg', etiket: 'JPEG görsel' },
  png: { tur: 'resim', destekleniyor: true, mime: 'image/png', uzanti: '.png', etiket: 'PNG görsel' },
  gif: { tur: 'resim', destekleniyor: false, mime: 'image/gif', uzanti: '.gif', etiket: 'GIF görsel' },
  bmp: { tur: 'resim', destekleniyor: false, mime: 'image/bmp', uzanti: '.bmp', etiket: 'BMP görsel' },
  webp: { tur: 'resim', destekleniyor: false, mime: 'image/webp', uzanti: '.webp', etiket: 'WEBP görsel' },
  docx: { tur: 'office', destekleniyor: true, mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', uzanti: '.docx', etiket: 'Word (.docx)' },
  xlsx: { tur: 'office', destekleniyor: true, mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', uzanti: '.xlsx', etiket: 'Excel (.xlsx)' },
  pptx: { tur: 'office', destekleniyor: true, mime: 'application/vnd.openxmlformats-officedocument.presentationml.presentation', uzanti: '.pptx', etiket: 'PowerPoint (.pptx)' },
  zip: { tur: 'diger', destekleniyor: false, mime: 'application/zip', uzanti: '.zip', etiket: 'ZIP arşivi' },
  eskiOffice: { tur: 'office', destekleniyor: true, mime: 'application/octet-stream', uzanti: '.doc', etiket: 'Eski Office belgesi (.doc / .xls)' },
  rtf: { tur: 'diger', destekleniyor: false, mime: 'application/rtf', uzanti: '.rtf', etiket: 'RTF belgesi' },
  html: { tur: 'metin', destekleniyor: false, mime: 'text/html', uzanti: '.html', etiket: 'HTML / XML içerik' },
  metin: { tur: 'metin', destekleniyor: false, mime: 'text/plain', uzanti: '.txt', etiket: 'Düz metin' },
  bos: { tur: 'bos', destekleniyor: false, mime: 'application/octet-stream', uzanti: '', etiket: 'Boş dosya' },
  bilinmiyor: { tur: 'diger', destekleniyor: false, mime: 'application/octet-stream', uzanti: '.bin', etiket: 'Tanınmayan dosya türü' }
};

export const dosyaTuruBul = (bytes) => {
  const b = bytes || new Uint8Array(0);
  if (b.length === 0) return TURLER.bos;

  // PDF imzası ilk 1024 byte içinde herhangi bir yerde olabilir (pdf.js de bunu kabul ediyor).
  if (icerir(b, '%PDF', 1024)) return TURLER.pdf;

  if (baslar(b, [0xFF, 0xD8, 0xFF])) return TURLER.jpg;
  if (baslar(b, [0x89, 0x50, 0x4E, 0x47])) return TURLER.png;
  if (baslar(b, [0x47, 0x49, 0x46, 0x38])) return TURLER.gif;
  // "BM" metinle de başlayabileceği için BMP başlığındaki ayrılmış (sıfır) alanlar da kontrol edilir.
  if (baslar(b, [0x42, 0x4D]) && baslar(b, [0, 0, 0, 0], 6)) return TURLER.bmp;
  if (baslar(b, [0x52, 0x49, 0x46, 0x46]) && baslar(b, [0x57, 0x45, 0x42, 0x50], 8)) return TURLER.webp;

  if (baslar(b, [0x50, 0x4B, 0x03, 0x04])) {
    if (icerir(b, 'word/')) return TURLER.docx;
    if (icerir(b, 'xl/')) return TURLER.xlsx;
    if (icerir(b, 'ppt/')) return TURLER.pptx;
    return TURLER.zip;
  }
  if (baslar(b, [0xD0, 0xCF, 0x11, 0xE0])) return TURLER.eskiOffice;
  if (icerir(b, '{\\rtf', 8)) return TURLER.rtf;

  // İlk bölüm çoğunlukla yazdırılabilir karakterse metin kabul edilir.
  const ornek = b.subarray(0, Math.min(b.length, 2048));
  let yazdirilabilir = 0;
  for (const x of ornek) {
    if (x === 9 || x === 10 || x === 13 || x >= 32) yazdirilabilir++;
  }
  if (yazdirilabilir / ornek.length > 0.95) {
    const bas = new TextDecoder('utf-8').decode(ornek.subarray(0, 64)).trimStart().toLowerCase();
    return bas.startsWith('<') ? TURLER.html : TURLER.metin;
  }

  return TURLER.bilinmiyor;
};

export const base64Bytes = (base64) => {
  const ikili = atob(base64);
  const bytes = new Uint8Array(ikili.length);
  for (let i = 0; i < ikili.length; i++) {
    bytes[i] = ikili.charCodeAt(i);
  }
  return bytes;
};

/** Blob URL'i verilen adla indirir. */
export const dosyaIndir = (url, dosyaAdi) => {
  const a = document.createElement('a');
  a.href = url;
  a.download = dosyaAdi;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};
